/**
 * order-ref.ts — jeden klucz zamówienia we froncie (Etap 3b, 2026-09-04; plan „Jeden klucz zamówienia", K5).
 *
 * Front zna tylko `public_id` (ULID z rejestru `order_identity`): jedna funkcja daje klucz do API,
 * adres i drawer. Stare postacie (UUID Allegro z prefiksem AL-, `1:106001`, `wc:4:47210`, `vh:2:X`)
 * wchodzą wyłącznie z zewnątrz (stare linki, zakładki, e-maile) i są rozwiązywane na granicy przez
 * `GET /api/orders/{id}/ref` — nigdy przez zgadywanie po dwukropku czy prefiksie.
 */
export const ULID_RE = /^[0-9A-HJKMNP-TV-Z]{26}$/i;

export interface OrderRefInfo {
  public_id: string | null;
  short_no?: number | null;
  source: string;         // allegro | manual | woocommerce | prestashop | erli | ebay | tiktok | vonhalsky
  table_source: string;   // allegro | prestashop | erli | ebay | tiktok | vonhalsky (manual/wc żyją w allegro)
  table?: string;
  pk: string;             // klucz tabeli źródłowej (to, po czym dziś ładują drawery)
  external_ref?: { source?: string; number?: string } | null;
  url: string;
  via?: string;
}

export function isUlid(v: any): boolean {
  return ULID_RE.test(String(v == null ? '' : v).trim());
}

/** Numer SellBase: same cyfry (nadawany w rejestrze, ≥ 5 cyfr dla nowych; stare Allegro mogą mieć mniej). */
export function isShortNo(v: any): boolean {
  return /^\d{1,12}$/.test(String(v == null ? '' : v).trim());
}

/** Numer SellBase z obiektu zamówienia / zlecenia / sprawy (short_no, order_short_no, entity_short_no). */
export function orderShortNo(o: any): string {
  if (!o || typeof o !== 'object') return '';
  const n = o.short_no ?? o.order_short_no ?? o.entity_short_no ?? null;
  return (n != null && isShortNo(n)) ? String(n) : '';
}

/** Klucz zamówienia do API i adresu: public_id, a w jego braku stary identyfikator bez prefiksu AL-. */
export function orderRef(o: any): string {
  if (o == null) return '';
  if (typeof o === 'string' || typeof o === 'number') return String(o).replace(/^AL-/i, '');
  const nr = orderShortNo(o);
  if (nr) return nr;  // 2026-09-04: numer SellBase = widoczny identyfikator i adres
  const pid = String(o.public_id || o.order_public_id || '').trim();
  if (pid && isUlid(pid)) return pid.toUpperCase();
  const legacy = o.allegro_id || o.source_order_id || o.order_id || o.id || '';
  return String(legacy || '').replace(/^AL-/i, '');
}

/** Kanoniczny adres zamówienia. */
/** Tożsamość SellBase z surowego wiersza źródła (public_id, short_no, external_ref, order_url) do
 * obiektu „allegro-like" mapperów (VH/Erli/eBay/TikTok/PS). 2026-09-08 (zgłoszenie właściciela:
 * /orders/vh%3A2%3A681T8A5 zamiast /orders/110054): mappery gubiły te pola, więc nagłówek drawera
 * i pasek adresu nie znały numeru SellBase, choć API go zwraca. */
export function tozsamoscZWiersza(raw: any): { public_id: string | null; short_no: number | null; external_ref: any; order_url: string } {
  const r = raw || {};
  const pid = String(r.public_id || '').trim().toUpperCase();
  const nr = (r.short_no != null && /^\d+$/.test(String(r.short_no))) ? parseInt(String(r.short_no), 10) : null;
  return {
    public_id: isUlid(pid) ? pid : null,
    short_no: nr,
    external_ref: (r.external_ref && typeof r.external_ref === 'object') ? r.external_ref : null,
    order_url: String(r.order_url || '') || (nr ? '/orders/' + nr : (isUlid(pid) ? '/orders/' + pid : '')),
  };
}

export function orderUrl(o: any): string {
  const ref = orderRef(o);
  return ref ? '/orders/' + encodeURIComponent(ref) : '/orders';
}

/** Który drawer obsługuje zamówienie: główny (Allegro/ręczne/WC/PS/VH) czy modułowy (Erli/eBay/TikTok). */
export function drawerTargetFor(info: { table_source?: string; source?: string; pk?: string } | null | undefined): { kind: string; pk: string } {
  const src = String((info && (info.table_source || info.source)) || '').toLowerCase();
  const pk = String((info && info.pk) || '');
  if (src === 'erli' || src === 'ebay' || src === 'tiktok') return { kind: src, pk };
  return { kind: 'main', pk };
}

const _cache: Record<string, OrderRefInfo | null> = {};

function _token(): string {
  const w = window as any;
  try { return (w.SellBaseAPI?.getAuthToken?.()) || localStorage.getItem('sellbase_token') || ''; } catch { return ''; }
}

/** Rozwiązanie dowolnej postaci id na tożsamość; null = brak (404), niejednoznaczne (409) albo błąd sieci. */
export async function resolveOrderRef(id: any, source?: string): Promise<OrderRefInfo | null> {
  const raw = String(id == null ? '' : id).trim();
  if (!raw) return null;
  const key = raw.toUpperCase() + '|' + String(source || '');
  if (key in _cache) return _cache[key];
  const url = '/api/orders/' + encodeURIComponent(raw) + '/ref' + (source ? '?source=' + encodeURIComponent(source) : '');
  // Chwilowe błędy (sieć, 401 przed odświeżeniem tokena, 5xx) nie są zapamiętywane — jedna ponowna próba;
  // zapamiętujemy tylko odpowiedzi ostateczne (200 albo 404/409).
  for (let proba = 0; proba < 2; proba++) {
    try {
      const r = await fetch(url, { headers: { 'Authorization': 'Bearer ' + _token() } });
      if (r.ok) {
        const info = (await r.json()) as OrderRefInfo;
        _cache[key] = info && info.pk ? info : null;
        return _cache[key];
      }
      if (r.status === 404 || r.status === 409) { _cache[key] = null; return null; }
    } catch { /* sieć — ponów */ }
    if (proba === 0) await new Promise(function(res) { setTimeout(res, 300); });
  }
  return null;
}

/**
 * Etykieta zlecenia dotyczącego zamówienia: nasze ID (entity_public_id, ULID) + kupujący z etykiety
 * zapisanej przy tworzeniu ("105342 · Client:40433929" → "#01M1… · Client:40433929"). Bez ULID — jak dotąd.
 */
export function taskEntityLabel(t: any): string {
  if (!t) return '';
  const stored = String(t.entity_label || t.entity_id || '').trim();
  const nr = orderShortNo(t);
  const pid = String(t.entity_public_id || '').trim().toUpperCase();
  if (!nr && !isUlid(pid)) return stored;
  const czesci = stored.split(' · ');
  const reszta = czesci.length > 1 ? czesci.slice(1).join(' · ') : '';
  return '#' + (nr || pid) + (reszta ? ' · ' + reszta : '');
}

/** Test/diagnostyka: wyczyść pamięć rozwiązań. */
export function _resetOrderRefCache(): void {
  for (const k of Object.keys(_cache)) delete _cache[k];
}

if (typeof window !== 'undefined') {
  const w = window as any;
  w.orderRef = orderRef; w.orderUrl = orderUrl; w.resolveOrderRef = resolveOrderRef; w.taskEntityLabel = taskEntityLabel; w.orderShortNo = orderShortNo;
  w.SellBaseApp = w.SellBaseApp || {};
  w.SellBaseApp.orderUrl = orderUrl;
}
